import type { CheckboxItem } from '@app/components/dialog-checkbox/domain';
import type { CreatorLabPageViewSource } from '@app/routes/content/analytics/events';
import { nanoid } from 'nanoid';

import type { EditListItem } from './components/edit-item';
import {
  CLIPS_COLORS,
  DEFAULT_ACTIVE_WORD_COLOR,
  HOPIN_WEB_DOMAINS,
} from './const';
import type {
  AdvancedEditCaptions,
  AdvancedEditCut,
  BrandingPreset,
  CaptionPosition,
  DraftClip,
  GetRecordingEditByIdApiResponse,
  RecordingEditEntity,
  RecordingEditListApiRow,
  TranscriptSegment,
} from './domain';
import { EDITOR_TABS, EditorTabs } from './domain';
import { WHITE } from './theme/theme-colors';

export type VideoTypeAnalyticsValue = 'recording' | 'clip' | 'uploaded';

export const createFallbackAdvancedEditCuts = ({
  recordingId,
  recordingDurationSec,
  cutId,
}: {
  recordingId: string;
  recordingDurationSec: number;
  cutId?: string;
}): AdvancedEditCut[] => {
  return [
    {
      id: cutId ?? `fallback-cut-${recordingId}`,
      recording_id: recordingId,
      start_time_ms: 0,
      end_time_ms: Math.max(0, Math.round(recordingDurationSec * 1000)),
      transition: { type: 'jump_cut' },
    },
  ];
};

export const resolveDraftCutsWithFallback = ({
  draftCuts,
  recordingId,
  recordingDurationSec,
  fallbackCutId,
}: {
  draftCuts: AdvancedEditCut[];
  recordingId?: string;
  recordingDurationSec: number;
  fallbackCutId?: string;
}): AdvancedEditCut[] => {
  if (draftCuts.length > 0) {
    return draftCuts;
  }

  if (!recordingId || recordingDurationSec <= 0) {
    return [];
  }

  return createFallbackAdvancedEditCuts({
    recordingId,
    recordingDurationSec,
    cutId: fallbackCutId,
  });
};

/**
 * Assigns stable frontend `id`s to cuts returned by the GET-by-ID API.
 * The API returns cuts without an `id` field (it is frontend-only); this
 * utility adds one using a deterministic `${editId}-cut-${index}` pattern so
 * React keys and segment rendering remain stable across re-renders.
 */
export const hydrateCutsFromApiResponse = (
  apiCuts: GetRecordingEditByIdApiResponse['cuts'],
  editId: string,
): AdvancedEditCut[] => {
  return apiCuts.map(cut => ({
    // Deterministic ID derived from the cut's content rather than its index.
    // Index-based IDs (`${editId}-cut-0`) change when the server reorders cuts,
    // causing React to remount every cut component and lose local state.
    // Using segment coordinates makes the ID stable across fetches and rerenders
    // as long as the same cut (recording, start, end) exists in the list.
    id: `${editId}-${cut.recording_id}-${cut.start_time_ms}-${cut.end_time_ms}`,
    recording_id: cut.recording_id,
    start_time_ms: cut.start_time_ms,
    end_time_ms: cut.end_time_ms,
    transition: cut.transition,
  }));
};

export const hydrateAdvancedEditCutsFromRow = ({
  selectedEditId,
  recordingId,
  recordingDurationSec,
  rowCuts,
}: {
  selectedEditId: string;
  recordingId: string;
  recordingDurationSec: number;
  rowCuts?: RecordingEditListApiRow['cuts'];
}): AdvancedEditCut[] => {
  const hydratedCuts =
    rowCuts?.map((cut, index) => ({
      id: `${selectedEditId}-cut-${index}`,
      recording_id: recordingId,
      start_time_ms: cut.start_time_ms,
      end_time_ms: cut.end_time_ms,
      transition: { type: 'jump_cut' as const },
    })) ?? [];

  const validCuts = hydratedCuts.filter(
    cut => cut.end_time_ms > cut.start_time_ms,
  );
  if (validCuts.length > 0) {
    return validCuts;
  }

  return createFallbackAdvancedEditCuts({
    recordingId,
    recordingDurationSec,
    cutId: `${selectedEditId}-cut-0`,
  });
};

/**
 * Determines the video type for analytics based on recording category and parent relationship
 */
export const getVideoType = (
  recording: {
    category: string;
    parent_recording_id: string | null;
  } | null,
): VideoTypeAnalyticsValue => {
  if (!recording) return 'recording';

  // If it has a parent, it's a clip
  if (recording.parent_recording_id) {
    return 'clip';
  }

  // If category is upload, it's uploaded
  if (recording.category === 'upload') {
    return 'uploaded';
  }

  // Otherwise it's a recording (main, backup, rehearsal, default)
  return 'recording';
};

/**
 * Converts seconds to HH:MM:SS format (shows hours only if present)
 */
export const formatSecondsToTimestamp = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
    .toString()
    .padStart(2, '0')}`;
};

/**
 * Formats time in milliseconds to HH:MM:SS format (shows hours only if present)
 */
export const formatTime = (timeMs: number): string => {
  const totalSeconds = Math.round(timeMs / 1000);
  return formatSecondsToTimestamp(totalSeconds);
};

/** Short duration label for clip/edit list cards (e.g. "2m", "45s"). */
export function formatClipDurationShort(
  startMs: number,
  endMs: number,
): string {
  const sec = Math.max(0, Math.floor((endMs - startMs) / 1000));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m >= 1) {
    return `${m}m`;
  }
  return `${s}s`;
}

/** Duration span across an advanced edit's cuts for list card labels. */
export function formatAdvancedEditDurationLabel(
  edit: RecordingEditEntity,
): string {
  const { cuts } = edit.draft;
  if (cuts.length === 0) {
    return formatClipDurationShort(0, 0);
  }
  const start = Math.min(...cuts.map(c => c.start_time_ms));
  const end = Math.max(...cuts.map(c => c.end_time_ms));
  return formatClipDurationShort(start, end);
}

/** Total active cut length in seconds (sum of each cut’s span). */
export function getAdvancedEditDurationSeconds(
  edit: RecordingEditEntity,
): number {
  const cuts = edit.draft?.cuts ?? [];
  const totalMs = cuts.reduce(
    (sum, cut) => sum + (cut.end_time_ms - cut.start_time_ms),
    0,
  );
  return totalMs / 1000;
}

export type EditListThumbnailContext = {
  recordingId: string | null | undefined;
  hlsUrl: string | null | undefined;
  enabled: boolean;
};

/**
 * Builds {@link EditListItem} rows for the sidebar from raw list rows plus
 * optional entities (duration when cuts exist). All formatting is for render.
 */
export function mapAdvancedEditSummariesToEditListItems(
  edits: RecordingEditListApiRow[],
  recordingThumbnailUrl: string | null,
  thumbnailContext?: EditListThumbnailContext,
): EditListItem[] {
  const getDurationLabel = (row: RecordingEditListApiRow): string => {
    if (row.cuts?.length) {
      const start = Math.min(...row.cuts.map(cut => cut.start_time_ms));
      const end = Math.max(...row.cuts.map(cut => cut.end_time_ms));
      return formatClipDurationShort(start, end);
    }

    return formatClipDurationShort(0, 0);
  };

  const getThumbnail = (
    row: RecordingEditListApiRow,
  ): EditListItem['thumbnail'] => {
    if (
      !thumbnailContext?.enabled ||
      !thumbnailContext.recordingId ||
      !thumbnailContext.hlsUrl
    ) {
      return null;
    }
    if (!row.cuts?.length) return null;
    const startMs = Math.min(...row.cuts.map(cut => cut.start_time_ms));
    return {
      recordingId: thumbnailContext.recordingId,
      hlsUrl: thumbnailContext.hlsUrl,
      timeMs: startMs,
    };
  };

  return edits.map(row => ({
    id: row.id,
    title: row.title.trim() !== '' ? row.title : 'Untitled Edit',
    durationLabel: getDurationLabel(row),
    thumbnailUrl: recordingThumbnailUrl,
    thumbnail: getThumbnail(row),
    status: row.status,
  }));
}

/**
 * Converts HH:MM:SS or MM:SS format to seconds
 */
export const parseTimestampToSeconds = (timestamp: string): number => {
  const parts = timestamp.split(':').map(Number);

  if (parts.length === 3) {
    // HH:MM:SS format
    const [hours, minutes, seconds] = parts;
    return (hours || 0) * 3600 + (minutes || 0) * 60 + (seconds || 0);
  } else if (parts.length === 2) {
    // MM:SS format
    const [minutes, seconds] = parts;
    return (minutes || 0) * 60 + (seconds || 0);
  }

  return 0;
};

/**
 * Converts draft clips to checkbox items for selection dialogs
 */
export const convertClipsToCheckboxItems = (
  clips: Omit<DraftClip, 'source_type'>[],
): CheckboxItem[] => {
  return clips.map(clip => {
    const startTime = formatTime(clip.start_time_ms);
    const endTime = formatTime(clip.end_time_ms);
    const timeRange = `${startTime} - ${endTime}`;

    return {
      id: clip.id,
      label: clip.title,
      description: timeRange,
      checked: true,
    };
  });
};

/**
 * Generates a consistent color index based on a clip ID
 * Uses character sum to map IDs to predefined color indices
 *
 * @param clipId - Unique identifier for the clip
 * @returns Color index (0-9) for consistent color assignment
 */
export const getColorIndex = (clipId: string): number => {
  let sum = 0;
  for (let i = 0; i < clipId.length; i++) {
    sum += clipId.charCodeAt(i);
  }
  return sum % CLIPS_COLORS.length;
};

/**
 * Gets the hex color value for a clip based on its color index
 *
 * @param colorIndex - Color index (0-9)
 * @returns Hex color value
 */
export const getClipHexColor = (colorIndex: number): string => {
  return CLIPS_COLORS[colorIndex % CLIPS_COLORS.length];
};

/**
 * Generates a unique clip ID using nanoid
 */
export const generateClipId = (): string => {
  return nanoid();
};

/** API payload shape for clip create/update. Only these fields are sent; add new keys here when the API supports them. */
export type ClipApiPayload = Pick<
  DraftClip,
  | 'id'
  | 'title'
  | 'start_time_ms'
  | 'end_time_ms'
  | 'aspect_ratio'
  | 'background_color'
  | 'preset_id'
  | 'include_captions'
  | 'captions_position'
  | 'captions_style'
  | 'captions_color'
  | 'highlight_color'
>;

/** Single source of truth for caption font options (id, name, fontFamily, fontWeight, fontScale). Used by font-style-selector, use-active-caption, and id ↔ fontFamily conversion. */
export type CaptionFontOption = {
  id: string;
  name: string;
  fontFamily: string;
  fontWeight: string;
  fontScale: number;
};

export const CAPTION_FONT_OPTIONS: CaptionFontOption[] = [
  {
    id: 'impact-bold',
    name: 'Impact Bold',
    fontFamily: 'Anton',
    fontWeight: '400',
    fontScale: 1.0,
  },
  {
    id: 'clean-pop',
    name: 'Clean Pop',
    fontFamily: 'Poppins',
    fontWeight: '600',
    fontScale: 0.9,
  },
  {
    id: 'elegant',
    name: 'Elegant',
    fontFamily: 'Alice',
    fontWeight: '400',
    fontScale: 1.0,
  },
  {
    id: 'comic-pop',
    name: 'Comic Pop',
    fontFamily: 'Bangers',
    fontWeight: '400',
    fontScale: 1.1,
  },
  {
    id: 'vlog',
    name: 'Vlog',
    fontFamily: 'Permanent Marker',
    fontWeight: '400',
    fontScale: 0.9,
  },
  {
    id: 'futuristic',
    name: 'Futuristic',
    fontFamily: 'Orbitron',
    fontWeight: '700',
    fontScale: 0.8,
  },
  {
    id: 'arcade',
    name: 'Arcade',
    fontFamily: 'Caveat',
    fontWeight: '400',
    fontScale: 1.2,
  },
  {
    id: 'epic',
    name: 'Epic',
    fontFamily: 'Amarante',
    fontWeight: '400',
    fontScale: 0.8,
  },
  {
    id: 'soft',
    name: 'Soft',
    fontFamily: 'Fredoka',
    fontWeight: '500',
    fontScale: 0.9,
  },
  {
    id: 'anchor',
    name: 'Anchor',
    fontFamily: 'Oswald',
    fontWeight: '600',
    fontScale: 1.0,
  },
];

/** Returns font family name for API when given a style id; otherwise returns the value as-is (e.g. if already font family from API). */
export const getFontFamilyForCaptionStyleId = (
  styleIdOrFontFamily: string | undefined,
): string | undefined => {
  if (!styleIdOrFontFamily) return undefined;
  const byId = CAPTION_FONT_OPTIONS.find(o => o.id === styleIdOrFontFamily);
  if (byId) return byId.fontFamily;
  return styleIdOrFontFamily;
};

/** Returns style id for UI when given captions_style from API (font family or style id). */
export const getCaptionStyleIdForApiValue = (
  apiValue: string | undefined,
): string | undefined => {
  if (!apiValue) return undefined;
  const byFont = CAPTION_FONT_OPTIONS.find(o => o.fontFamily === apiValue);
  if (byFont) return byFont.id;
  return apiValue;
};

/** Converts API captions into draft shape (style id for UI). */
export const hydrateCaptionsFromApiResponse = (
  captions: AdvancedEditCaptions,
): AdvancedEditCaptions => {
  if (!captions.enabled) {
    return { enabled: false };
  }
  return {
    ...captions,
    captions_style:
      getCaptionStyleIdForApiValue(captions.captions_style) ??
      captions.captions_style,
  };
};

/** Maps a legacy draft clip's caption fields to edit draft captions. */
export const mapClipCaptionsToEditCaptions = (
  clip: DraftClip,
  presets: BrandingPreset[],
  defaultPresetId?: string,
): AdvancedEditCaptions => {
  if (!clip.include_captions) {
    return { enabled: false };
  }
  const presetId = clip.preset_id ?? defaultPresetId ?? '';
  const preset =
    getCurrentPreset(presetId, presets) ?? getDefaultPreset(presets);
  const position = clip.captions_position as CaptionPosition | undefined;
  return {
    enabled: true,
    highlight_color: clip.highlight_color ?? DEFAULT_ACTIVE_WORD_COLOR,
    captions_style:
      getCaptionStyleIdForApiValue(clip.captions_style) ?? 'impact-bold',
    captions_position: position ?? 'bottom',
    preset_id: presetId,
    captions_color:
      clip.captions_color ??
      getPresetColor(preset ?? null, ['colorWhite']) ??
      WHITE,
  };
};

/** Builds captions payload for PUT (font family + derived colour). */
export const formatCaptionsForPut = (
  captions: AdvancedEditCaptions,
  presets: BrandingPreset[],
): AdvancedEditCaptions => {
  if (!captions.enabled) {
    return { enabled: false };
  }
  const preset =
    getCurrentPreset(captions.preset_id, presets) ?? getDefaultPreset(presets);
  return {
    enabled: true,
    highlight_color: captions.highlight_color,
    captions_style:
      getFontFamilyForCaptionStyleId(captions.captions_style) ??
      captions.captions_style,
    captions_color: getPresetColor(preset ?? null, ['colorWhite']) ?? WHITE,
    captions_position: captions.captions_position,
    preset_id: captions.preset_id,
  };
};

/**
 * Converts DraftClip to API payload format.
 * Inclusion-based: only fields in ClipApiPayload are sent; new frontend-only DraftClip fields are ignored by default.
 * Sends captions_style as font family name for the API.
 */
export const getClipApiPayload = (clip: DraftClip): ClipApiPayload => ({
  id: clip.id,
  title: clip.title,
  start_time_ms: clip.start_time_ms,
  end_time_ms: clip.end_time_ms,
  aspect_ratio: clip.aspect_ratio ?? '16_9',
  background_color: clip.background_color,
  preset_id: clip.preset_id,
  include_captions: clip.include_captions ?? false,
  captions_position: clip.captions_position,
  captions_style: getFontFamilyForCaptionStyleId(clip.captions_style),
  captions_color: clip.captions_color,
  highlight_color: clip.highlight_color,
});

/**
 * Calculates the height of a text container based on its width and options
 */
export interface TextMeasurementOptions {
  fontSize: number;
  fontFamily?: string;
  fontWeight?: string | number;
  lineHeight?: number;
}

export function calculateTextContainerHeight(
  text: string,
  containerWidth: number,
  options: TextMeasurementOptions,
): number {
  const {
    fontSize,
    fontFamily = 'Arial',
    fontWeight = 'normal',
    lineHeight,
  } = options;

  // Create temporary element
  const element = document.createElement('div');
  element.style.position = 'absolute';
  element.style.visibility = 'hidden';
  element.style.height = 'fit-content';
  element.style.width = `${containerWidth}px`;
  element.style.fontSize = `${fontSize}px`;
  element.style.fontFamily = fontFamily;
  element.style.fontWeight = fontWeight.toString();
  element.style.lineHeight = lineHeight ? `${lineHeight}px` : 'normal';
  element.style.wordWrap = 'break-word';
  element.style.whiteSpace = 'normal';
  element.textContent = text;

  document.body.appendChild(element);

  const height = element.offsetHeight;

  document.body.removeChild(element);

  return height;
}

/**
 * Gets the display name for a speaker from the speakers mapping
 * @param speakerKey - The speaker key from transcript
 * @param speakersMapping - The speakers mapping from state
 * @returns Display name (firstName lastName) or fallback to speaker key
 */
export const getSpeakerDisplayName = (
  speakerKey: string,
  speakersMapping: Record<string, any> | undefined,
): string => {
  const speakerData = speakersMapping?.[speakerKey];

  if (!speakerData) {
    return speakerKey; // Fallback to speaker key
  }

  if (speakerData.firstName || speakerData.lastName) {
    return `${speakerData.firstName || ''} ${
      speakerData.lastName || ''
    }`.trim();
  }

  return speakerKey; // Fallback to speaker key
};

/**
 * Filters transcript segments based on search query
 * Searches in both segment text and speaker display names
 * @param segments - Array of transcript segments to filter
 * @param searchQuery - Search query string
 * @param speakersMapping - The speakers mapping from state
 * @returns Filtered array of segments matching the search query
 */
export const filterTranscriptSegments = (
  segments: TranscriptSegment[],
  searchQuery: string,
  speakersMapping: Record<string, any> | undefined,
): TranscriptSegment[] => {
  if (!searchQuery) {
    return segments;
  }

  const lowerSearchQuery = searchQuery.toLowerCase();

  return segments.filter(segment => {
    const textMatches = segment.text.toLowerCase().includes(lowerSearchQuery);

    const displaySpeakerName = getSpeakerDisplayName(
      segment.speaker || '',
      speakersMapping,
    );
    const speakerMatches = displaySpeakerName
      .toLowerCase()
      .includes(lowerSearchQuery);

    return textMatches || speakerMatches;
  });
};

/**
 * Gets the current preset from a clip's preset ID or falls back to default preset
 * @param presetId - The clip's preset_id from API
 * @param presets - Array of available branding presets
 * @returns The matching preset, default preset, or first available preset
 */
export const getCurrentPreset = (
  presetId: string | undefined,
  presets: BrandingPreset[],
): BrandingPreset | null => {
  // If clip has a preset ID, find it in presets list
  if (presetId) {
    const clipPreset = presets.find(p => p.id === presetId);
    if (clipPreset) return clipPreset;
  }
  // Fallback to default preset or first available
  return presets.find(p => p.isDefault) || presets[0] || null;
};

/**
 * Gets the default preset from a list of presets
 * @param presets - Array of branding presets
 * @returns The default preset or first available preset
 */
export const getDefaultPreset = <T extends { id: string; isDefault?: boolean }>(
  presets: T[],
): T | undefined => {
  return presets.find(p => p.isDefault) ?? presets[0];
};

/**
 * Safely extracts a color string from preset config
 * Handles the config's union type (string | Record<string, string>)
 * @param preset - The branding preset
 * @param colorKeys - Array of color keys to try in order (first match wins)
 * @returns The color string or undefined
 */
export const getPresetColor = (
  preset: BrandingPreset | null,
  colorKeys: string[],
): string | undefined => {
  if (!preset?.config) return undefined;

  for (const key of colorKeys) {
    const value = preset.config[key];
    if (typeof value === 'string') return value;
  }
  return undefined;
};

/**
 * Sets the default preset ID on a clip if it doesn't have one
 * Updates the clip's preset_id property
 * @param clip - The clip to update
 * @param presets - Array of branding presets
 */
export const setDefaultPresetOnClip = (
  clip: DraftClip,
  presets: Array<{ id: string; isDefault?: boolean }>,
): void => {
  if (clip.preset_id) return; // Already has a preset

  const defaultPreset = getDefaultPreset(presets);
  if (!defaultPreset) return;

  clip.preset_id = defaultPreset.id;
};

/**
 * Gets the background color from a clip's preset
 * Used for initializing lastUsedBackgroundColor and blur toggle fallback
 * @param presetId - The preset ID from the clip
 * @param presets - Array of branding presets
 * @returns The preset's background color or undefined
 */
export const getClipPresetBackgroundColor = (
  presetId: string | undefined,
  presets: BrandingPreset[],
): string | undefined => {
  const preset = getCurrentPreset(presetId, presets);
  return getPresetColor(preset, ['colorWhite', 'colorPrimary400']);
};

/** 1st preset colour (BRAND-PRIMARY / colorPrimary400) — active word highlight. */
export const getClipPresetActiveWordColor = (
  presetId: string | undefined,
  presets: BrandingPreset[],
): string => {
  const preset = getCurrentPreset(presetId, presets);
  return (
    getPresetColor(preset, ['colorPrimary400']) ?? DEFAULT_ACTIVE_WORD_COLOR
  );
};

/** Caption text colour from preset (NEUTRAL-PRIMARY / colorWhite). */
export const getClipPresetCaptionsColor = (
  presetId: string | undefined,
  presets: BrandingPreset[],
): string => {
  const preset = getCurrentPreset(presetId, presets);
  return getPresetColor(preset, ['colorWhite']) ?? WHITE;
};

// API properties that should only update selectedClip (not draft_clips)
// to allow hasClipUpdated to detect changes
type ApiClipProperty =
  | 'aspect_ratio'
  | 'background_color'
  | 'preset_id'
  | 'include_captions'
  | 'captions_position'
  | 'captions_style'
  | 'highlight_color'
  | 'isCustomActiveWordColor';

/**
 * Syncs a clip API property to selectedClip
 * Only updates selectedClip (not draft_clips) to allow hasClipUpdated to detect changes
 * @param selectedClip - The currently selected clip
 * @param propertyPath - The property to update
 * @param value - The value to set
 */
export const syncClipProperty = (
  selectedClip: DraftClip | null,
  propertyPath: ApiClipProperty,
  value: DraftClip[ApiClipProperty],
): void => {
  if (!selectedClip) return;
  (selectedClip as any)[propertyPath] = value;
};

/**
 * Syncs lastUsedBackgroundColor to both selectedClip and draft_clips
 * This is a frontend-only property that preserves color when blur is toggled
 */
export const syncLastUsedBackgroundColor = (
  selectedClip: DraftClip | null,
  draftClips: DraftClip[],
  color: string,
): void => {
  if (!selectedClip) return;

  selectedClip.lastUsedBackgroundColor = color;

  // Also update in draft_clips to ensure persistence
  const clipIndex = draftClips.findIndex(c => c.id === selectedClip.id);
  if (clipIndex !== -1) {
    draftClips[clipIndex].lastUsedBackgroundColor = color;
  }
};

// Extracts plain text from HTML string
export const extractPlainTextFromHtml = (html: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

// Removes background colors and text colors from HTML for clipboard
export const cleanHtmlForClipboard = (html: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const allElements = tempDiv.querySelectorAll('*');
  allElements.forEach(element => {
    const htmlElement = element as HTMLElement;
    htmlElement.style.removeProperty('background-color');
    htmlElement.style.removeProperty('background');
    htmlElement.style.removeProperty('color');
  });

  return tempDiv.innerHTML;
};

export const getEditorTabFromUrl = (): EditorTabs | null => {
  const url = new URL(window.location.href);
  const urlParams = url.searchParams;
  const tabParam = urlParams.get('editor-tab');

  const editorTab =
    tabParam && EDITOR_TABS.includes(tabParam as EditorTabs)
      ? (tabParam as EditorTabs)
      : null;

  return editorTab;
};

/** Portrait video player: width thresholds for responsive timer display (full / remaining / none) */
export const PORTRAIT_TIMER_MIN_WIDTH_FULL = 380;
export const PORTRAIT_TIMER_MIN_WIDTH_REMAINING = 290;

export type PortraitTimerDisplayMode = 'full' | 'remaining' | 'none';

/**
 * Resolves portrait timer display mode from width. Used only in portrait mode;
 * in landscape the return value is not used (caller may pass isPortrait to avoid work).
 */
export const getPortraitTimerDisplayMode = (
  isPortrait: boolean,
  portraitWidth: number,
): PortraitTimerDisplayMode => {
  if (!isPortrait) return 'full';
  if (portraitWidth >= PORTRAIT_TIMER_MIN_WIDTH_FULL) return 'full';
  if (portraitWidth >= PORTRAIT_TIMER_MIN_WIDTH_REMAINING) return 'remaining';
  return 'none';
};

export const inferSourceFromReferrer = (): CreatorLabPageViewSource => {
  try {
    const referrer = new URL(document.referrer);
    if (!HOPIN_WEB_DOMAINS.has(referrer.hostname)) return 'direct';
    if (referrer.pathname.includes('/emails'))
      return 'event-dashboard-email-page';
    return 'event-dashboard-recording-page';
  } catch {
    return 'direct';
  }
};
